import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "../firebase";

const LOADING_GIF = "/assets/images/globegif.gif";

// Shows the loading gif in place of an image until the real image
// has actually finished fetching, then swaps to it. Same as the
// LazyImg used in HomeScreen.jsx, kept consistent across components.
const LazyImg = ({ src, alt, className = "", ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    setLoaded(false);
    let cancelled = false;
    const preloader = new window.Image();
    preloader.src = src;
    preloader.onload = () => {
      if (!cancelled) setLoaded(true);
    };
    preloader.onerror = () => {
      if (!cancelled) setLoaded(true); // fall through so it doesn't stay stuck on gif forever
    };
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <img
      src={loaded ? src : LOADING_GIF}
      alt={alt}
      className={`${className} ${!loaded ? "lazy-img-loading" : ""}`.trim()}
      loading="lazy"
      {...rest}
    />
  );
};

// ══════════════════════════════════════════════
// IMAGE CAROUSEL (card ke andar)
// ══════════════════════════════════════════════
function ImageCarousel({ images, name }) {
  const [current, setCurrent] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div style={{ height: 240, background: "linear-gradient(135deg,#cfe8f3,#a8d8ea)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem" }}>
        🌍
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: 240, overflow: "hidden" }}>
      <LazyImg
        src={images[current]}
        alt={name}
        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s" }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c - 1 + images.length) % images.length); }}
            style={carouselBtnStyle("left")}
          >‹</button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c + 1) % images.length); }}
            style={carouselBtnStyle("right")}
          >›</button>
          <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 5 }}>
            {images.map((_, i) => (
              <span
                key={i}
                onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                style={{ width: 7, height: 7, borderRadius: "50%", background: i === current ? "#fff" : "rgba(255,255,255,0.5)", cursor: "pointer", display: "inline-block" }}
              />
            ))}
          </div>
          <div style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: "0.75rem", padding: "3px 8px", borderRadius: 20, backdropFilter: "blur(4px)" }}>
            📷 {images.length}
          </div>
        </>
      )}
    </div>
  );
}

function carouselBtnStyle(side) {
  return {
    position: "absolute",
    top: "50%",
    [side]: 8,
    transform: "translateY(-50%)",
    background: "rgba(0,0,0,0.45)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    fontSize: "1.2rem",
    cursor: "pointer",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

// ══════════════════════════════════════════════
// MAIN DESTINATION COMPONENT
// ══════════════════════════════════════════════
const Destination = ({ previewMode = false }) => {
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const q = previewMode
          ? query(collection(db, "destinations"), where("isActive", "==", true), limit(4))
          : query(collection(db, "destinations"), where("isActive", "==", true));
        const snap = await getDocs(q);
        setDestinations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching destinations:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, [previewMode]);

  if (loading) {
    return (
      <section className="destinations-section">
        <p style={{ textAlign: "center", color: "#64748b", padding: "2rem" }}>Loading destinations...</p>
      </section>
    );
  }

  if (destinations.length === 0) {
    return (
      <section className="destinations-section">
        <p style={{ textAlign: "center", color: "#64748b", padding: "2rem" }}>No destinations available.</p>
      </section>
    );
  }

  return (
    <section className="destinations-section">
      {previewMode ? (
        <div className="section-header">
          <div>
            <p className="section-eyebrow">🌍 Top Picks</p>
            <h2 className="section-title">Popular Destinations</h2>
          </div>
          <button className="section-viewall-btn" onClick={() => navigate("/destinations")}>
            View All Destinations <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      ) : (
        <h2>Popular Destinations</h2>
      )}

      <div className="destinations-grid">
        {destinations.map((dest) => {
          const location = [dest.state, dest.country].filter(Boolean).join(", ");
          return (
            <div
              className="destination-card"
              key={dest.id}
              onClick={() => navigate(`/destinations/${dest.id}`)}
              style={{ cursor: "pointer" }}
            >
              <ImageCarousel images={dest.imageUrls} name={dest.name} />
              <div className="card-content">
                {location && <p className="dest-location">📍 {location}</p>}
                <h3>{dest.name}</h3>
                {dest.tagline && (
                  <p style={{ color: "#64748b", fontSize: "0.88rem", fontStyle: "italic", margin: "4px 0" }}>"{dest.tagline}"</p>
                )}
                {dest.bestTimeToVisit && (
                  <p style={{ fontSize: "0.85rem", color: "#64748b" }}>🗓 Best time: {dest.bestTimeToVisit}</p>
                )}
                {dest.linkedTourIds?.length > 0 && (
                  <p style={{ fontSize: "0.85rem", color: "#00b4d8", fontWeight: 600 }}>
                    🗺️ {dest.linkedTourIds.length} Tour{dest.linkedTourIds.length > 1 ? "s" : ""} Available
                  </p>
                )}
                <button
                  className="card-btn"
                  onClick={(e) => { e.stopPropagation(); navigate(`/destinations/${dest.id}`); }}
                >
                  View Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {previewMode && (
        <div className="section-bottom-viewall">
          <button className="section-viewall-bottom-btn" onClick={() => navigate("/destinations")}>
            <i className="fas fa-globe"></i> View All Destinations
          </button>
        </div>
      )}
    </section>
  );
};

export default Destination;